import React, { createContext, useState, useCallback, useEffect } from 'react';
import axiosInstance from '../utility/axios';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(() => {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('userData');
        return !!(token && userData);
    });
    
    const [user, setUser] = useState(() => {
        const userData = localStorage.getItem('userData');
        return userData ? JSON.parse(userData) : null;
    });

    // Add a dedicated isAdmin state that derives from user data
    const [isAdmin, setIsAdmin] = useState(() => {
        const userData = localStorage.getItem('userData');
        if (!userData) return false;
        const parsedUser = JSON.parse(userData);
        return parsedUser?.isAdmin === true;
    });

    useEffect(() => {
        // Check for existing user data in localStorage on mount
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
                setIsAdmin(parsedUser?.isAdmin === true);
                setIsLoggedIn(true);
            } catch (error) {
                console.error('Error parsing stored user data', error);
                localStorage.removeItem('user');
            }
        }
    }, []);

    const login = useCallback((userData) => {
        // Ensure we're getting the correct structure
        const userToStore = {
            ...userData.user,
            isAdmin: userData.user?.role?.toLowerCase() === 'admin' || userData.user?.isAdmin || false
        };
        
        console.log('Stored user data:', userToStore); // Debug log
        
        localStorage.setItem('authToken', userData.token);
        localStorage.setItem('userData', JSON.stringify(userToStore));
        setIsLoggedIn(true);
        setUser(userToStore);
        setIsAdmin(userToStore.isAdmin === true);
    }, []);

    const logout = useCallback(async () => {
        try {
            // Call logout API endpoint if needed
            await fetch('/api/logout/', {
                method: 'POST',
                credentials: 'include',
            });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear user data regardless of API success
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            setIsLoggedIn(false);
            setUser(null);
            setIsAdmin(false);
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
    }, []);

    // Add this updateUser function to update the user state
    const updateUser = useCallback((updatedUserData) => {
        setUser(prevUser => {
            const updatedUser = { ...prevUser, ...updatedUserData };
            
            // Check if admin status needs to be updated
            const newIsAdmin = updatedUser?.isAdmin === true || 
                             updatedUser?.role?.toLowerCase() === 'admin';
            
            // Also update in localStorage to persist changes
            localStorage.setItem('userData', JSON.stringify({
                ...updatedUser,
                isAdmin: newIsAdmin
            }));
            
            return updatedUser;
        });
    }, []);
    
    // Derive isAdmin from user state changes to avoid nested state updates
    useEffect(() => {
        if (user) {
            const newIsAdmin = user?.isAdmin === true || user?.role?.toLowerCase() === 'admin';
            setIsAdmin(newIsAdmin);
        }
    }, [user]);

    // Reconcile role from server on mount to pick up any admin changes since last login
    useEffect(() => {
        if (!isLoggedIn) return;
        axiosInstance.get('/api/profile/')
            .then(res => {
                const role = res.data?.profile?.role;
                if (role !== undefined) {
                    const serverIsAdmin = role?.toLowerCase() === 'admin';
                    setUser(prev => {
                        if (!prev) return prev;
                        const updated = { ...prev, role, isAdmin: serverIsAdmin };
                        localStorage.setItem('userData', JSON.stringify(updated));
                        return updated;
                    });
                    setIsAdmin(serverIsAdmin);
                }
            })
            .catch(() => {});
    }, [isLoggedIn]);

    return (
        <AuthContext.Provider value={{ isLoggedIn, user, isAdmin, login, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};